import { Users, Lightbulb, Zap, Clock } from "lucide-react";

const steps = [
  { icon: Users, title: "Consultation", description: "Understanding your needs", color: "eco-green" },
  { icon: Lightbulb, title: "Design", description: "Custom solution tailored to you", color: "primary" },
  { icon: Zap, title: "Deploy", description: "Quick implementation", color: "sky-blue" },
  { icon: Clock, title: "Support", description: "24/7 assistance", color: "purple" },
];

const colorMap: Record<string, { iconBg: string; text: string; border: string; glow: string; badge: string }> = {
  "eco-green": {
    iconBg: "from-eco-green to-eco-green-light",
    text: "text-eco-green",
    border: "border-eco-green/30 hover:border-eco-green/50",
    glow: "bg-eco-green/20",
    badge: "bg-eco-green text-white",
  },
  "primary": {
    iconBg: "from-primary to-amber-glow",
    text: "text-primary",
    border: "border-primary/30 hover:border-primary/50",
    glow: "bg-primary/20",
    badge: "bg-primary text-primary-foreground",
  },
  "sky-blue": {
    iconBg: "from-sky-blue to-sky-blue-light",
    text: "text-sky-blue",
    border: "border-sky-blue/30 hover:border-sky-blue/50",
    glow: "bg-sky-blue/20",
    badge: "bg-sky-blue text-white",
  },
  "purple": {
    iconBg: "from-purple to-rose",
    text: "text-purple",
    border: "border-purple/30 hover:border-purple/50",
    glow: "bg-purple/20",
    badge: "bg-purple text-white",
  },
};

const ProjectProcess = () => {
  return (
    <section className="py-16 relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/30 to-background" />
      <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-eco-green/10 blur-3xl animate-pulse" />
      <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-sky-blue/10 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-eco-green/20 to-sky-blue/20 text-eco-green text-sm font-semibold mb-4 border border-eco-green/30">
            How We Work
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">Our Process</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {steps.map((step, index) => {
            const colors = colorMap[step.color];
            return (
              <div 
                key={index} 
                className={`group text-center p-6 rounded-2xl bg-card/50 backdrop-blur-sm border ${colors.border} transition-all duration-300 hover:-translate-y-2 hover:shadow-xl relative overflow-hidden`}
              >
                {/* Glow effect */}
                <div className={`absolute -top-10 -right-10 w-24 h-24 rounded-full ${colors.glow} blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                <div className="relative z-10">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors.iconBg} flex items-center justify-center mx-auto mb-4 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <step.icon className="w-7 h-7 text-white" />
                    <span className={`absolute -top-2 -right-2 w-6 h-6 rounded-full ${colors.badge} text-xs font-bold flex items-center justify-center shadow-md`}>
                      {index + 1}
                    </span>
                  </div>
                  <h3 className={`font-bold text-lg ${colors.text} mb-1`}>{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProjectProcess;
